// Types each drafted section in at its marker in his drawn Word draft, line by
// line as the download sets them, text only. Only sections whose lines changed
// are retyped, so streaming sections don't make the page flicker.
import { type DraftLine, type DraftMarkerLayout } from "@app_schema";

type Line = Pick<
  DraftLine,
  | "text"
  | "inChat"
  | "spaced"
  | "style"
  | "bold"
  | "italic"
  | "underline"
  | "leftIndent"
  | "hangingIndent"
  | "prefix"
  | "runs"
>;

// How his draft sets the paragraphs at the marker, as the server reads it for
// the downloaded file: whether an empty paragraph sets his apart, and whether
// his paragraph is on a list of its own, which the section's lines drop.
type Layout = Pick<DraftMarkerLayout, "separated" | "numbered">;

export type DraftFill = {
  marker: number;
  lines: ReadonlyArray<Line>;
  layout?: Layout;
};

type Placed = {
  paragraph: Element;
  original: Node;
  added: Element[];
  key: string;
  start: Element;
  end: Element;
};

// The first and last element a placed section filled.
export type PlacedSection = Pick<Placed, "start" | "end">;

export type DraftFills = Map<number, Placed>;

export const FILL_CLASS = "draft-fill";

// A section not yet drafted: its marker drawn as a tinted block named for it.
// The marker's text stays, for the fill to find, under the label: hidden over
// the size his run sets.
export const SLOT_CLASS = "draft-slot";
// The bookmark that starts each block of his file in the display copy.
export const BLOCK_PREFIX = "wf-b-";
// A slot's look, shared with easy setup's preview (see use_setup_slots.ts).
export const SLOT_LOOK =
  "padding: 6px 10px; border: 1px dashed #c4b5fd; border-radius: 4px; background: #f5f3ff; color: #6d28d9; font: 600 10.5px/1.4 ui-sans-serif, system-ui, sans-serif; letter-spacing: 0.08em; text-transform: uppercase;";
const SLOT_STYLE = `.${SLOT_CLASS} { font-size: 0 !important; } .${SLOT_CLASS}::before { content: attr(data-label); display: block; ${SLOT_LOOK} }`;
export const FILL_STYLE = `.${FILL_CLASS} { background: #f5f3ff; box-shadow: 0 0 0 2px #f5f3ff; border-radius: 2px; white-space: pre-wrap; } ${SLOT_STYLE}`;

// The label each section not yet drafted shows at its marker, by marker.
export type DraftSlots = ReadonlyMap<number, string>;

const token = (marker: number) => `[[DRAFT\u00A0${String(marker)}]]`;

const findToken = (root: Element, marker: number) => {
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
  );
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeValue === token(marker)) return node as Text;
  }
  return null;
};

// A copy of his empty paragraph, less the block id Insert here finds his
// paragraphs by: the copy isn't one of his.
const gapLike = (separator: Element) => {
  const gap = separator.cloneNode(true) as Element;
  gap
    .querySelectorAll(`[id^="${BLOCK_PREFIX}"]`)
    .forEach((mark) => mark.remove());
  return gap;
};

const emptyParagraph = (element: Element | null) =>
  element?.localName === "p" && !element.textContent?.trim() ? element : null;

// One of his runs: bold, italic and underline as he set them.
const runSpan = (doc: Document, run: Line["runs"][number]) => {
  const span = doc.createElement("span");
  if (run.bold) span.style.fontWeight = "bold";
  if (run.italic) span.style.fontStyle = "italic";
  if (run.underline) span.style.textDecorationLine = "underline";
  span.textContent = run.text;
  return span;
};

// His marker's font and size, less the bold and highlight that mark it, as in
// the downloaded file, with the line's own marks and its runs.
const filled = (
  template: Element,
  line: Line,
  marker: number,
  index: number,
) => {
  const span = template.cloneNode(false) as HTMLElement;
  span.classList.remove(SLOT_CLASS);
  delete span.dataset.label;
  span.classList.add(FILL_CLASS);
  span.dataset.wfMarker = String(marker);
  span.dataset.wfLine = String(index);
  span.style.fontWeight = line.bold ? "bold" : "";
  span.style.fontStyle = line.italic ? "italic" : "";
  span.style.textDecorationLine = line.underline ? "underline" : "";
  span.style.backgroundColor = line.inChat ? "yellow" : "";
  span.replaceChildren(
    line.prefix,
    ...line.runs.map((run) => runSpan(template.ownerDocument, run)),
  );
  return span;
};

// The viewer's class for one of his paragraph styles, and the prefixes of
// the classes it gives a paragraph's style and list numbering. Any blank, not
// just a space, becomes a dash: a class can't hold one.
const STYLE_CLASS = "docx_";
const NUMBERING_CLASS = "docx-num-";
const styleClass = (style: string) =>
  `${STYLE_CLASS}${style
    .replace(/[\s.]+/g, "-")
    .replace(/[&]+/g, "and")
    .toLowerCase()}`;

const dropClasses = (paragraph: Element, prefix: string) => {
  paragraph.classList.remove(
    ...[...paragraph.classList].filter((name) => name.startsWith(prefix)),
  );
};

// The line's paragraph as the download sets it: the style the server names in
// place of his (and that style's list numbering with it), his own list
// numbering dropped, and a list item written as text hanging.
const setParagraph = (
  paragraph: HTMLElement,
  line: Line,
  layout: Layout | undefined,
) => {
  if (layout?.numbered || line.style) dropClasses(paragraph, NUMBERING_CLASS);
  if (line.style) {
    dropClasses(paragraph, STYLE_CLASS);
    paragraph.classList.add(styleClass(line.style));
  }
  if (line.leftIndent != null && line.hangingIndent != null) {
    paragraph.style.marginInlineStart = `${String(line.leftIndent)}pt`;
    paragraph.style.textIndent = `-${String(line.hangingIndent)}pt`;
  }
};

const keyOf = (lines: ReadonlyArray<Line>) => JSON.stringify(lines);

// Undoes the fills of `markers`, earliest placed first: two in one paragraph
// fill the same element, and the earliest saved it before either, so restoring
// that one puts it back whole (the later restore then lands on nothing).
const undo = (fills: DraftFills, markers: ReadonlySet<number>) => {
  [...fills.keys()]
    .filter((marker) => markers.has(marker))
    .forEach((marker) => {
      const fill = fills.get(marker);
      if (!fill) return;

      fill.added.forEach((node) => node.remove());
      fill.paragraph.replaceWith(fill.original);
      fills.delete(marker);
    });
};

// The sections to type in again: those that changed or are gone, and every
// other placed in a paragraph one of them is in.
const stale = (fills: DraftFills, sections: ReadonlyArray<DraftFill>) => {
  const keys = new Map(
    sections.map(({ marker, lines }) => [marker, keyOf(lines)]),
  );
  const changed = [...fills].filter(
    ([marker, fill]) => keys.get(marker) !== fill.key,
  );
  const paragraphs = new Set(changed.map(([, fill]) => fill.paragraph));
  return new Set(
    [...fills]
      .filter(([, fill]) => paragraphs.has(fill.paragraph))
      .map(([marker]) => marker),
  );
};

// Where a placed section starts as he reads it: a section filling its own
// paragraph sits under his heading, so that's the top, past any blank lines.
export const sectionTop = (start: Element) => {
  const paragraph = start.closest("p");
  if (!paragraph || paragraph.firstChild !== start) return paragraph ?? start;

  let above = paragraph.previousElementSibling;
  while (above && !above.textContent?.trim())
    above = above.previousElementSibling;
  return above ?? paragraph;
};

// @returns the first and last filled element of each section placed, by marker
export const fillDraft = (
  root: Element,
  sections: ReadonlyArray<DraftFill>,
  fills: DraftFills,
) => {
  undo(fills, stale(fills, sections));
  const placed = new Map<number, PlacedSection>();

  sections.forEach(({ marker, lines, layout }) => {
    const kept = fills.get(marker);
    if (kept) {
      placed.set(marker, kept);
      return;
    }

    const node = findToken(root, marker);
    const [first, ...rest] = lines;
    const template = node?.parentElement;
    const paragraph = template?.closest("p");
    if (!node || !paragraph || !template || first === undefined) return;

    const original = paragraph.cloneNode(true);
    // The empty paragraph after his, which the section's paragraphs copy. A
    // section filled earlier in the same paragraph may stand there now: only
    // an empty paragraph is copied, as the download checks.
    const separator = layout?.separated
      ? emptyParagraph(paragraph.nextElementSibling)
      : null;
    const start = filled(template, first, marker, 0);
    // His block's bookmark stays, so the block keeps its number; after the
    // section, so the section still starts the paragraph (see sectionTop).
    const bookmark = paragraph.querySelector(`[id^="${BLOCK_PREFIX}"]`);
    paragraph.replaceChildren(start, ...(bookmark ? [bookmark] : []));
    setParagraph(paragraph as HTMLElement, first, layout);

    const added = rest.flatMap((line, index) => {
      const next = original.cloneNode(false) as HTMLElement;
      setParagraph(next, line, layout);
      next.append(filled(template, line, marker, index + 1));
      if (!line.spaced || !separator) return [next];
      return [gapLike(separator), next];
    });
    paragraph.after(...added);
    const fill = {
      paragraph,
      original,
      added,
      key: keyOf(lines),
      start,
      end: added.at(-1) ?? start,
    };
    fills.set(marker, fill);
    placed.set(marker, fill);
  });

  return placed;
};

// Labels each marker still in the draft with its section, a tinted block.
export const markSlots = (root: Element, slots: DraftSlots) => {
  slots.forEach((label, marker) => {
    const holder = findToken(root, marker)?.parentElement;
    if (!holder) return;

    holder.classList.add(SLOT_CLASS);
    holder.dataset.label = label;
    holder.dataset.wfMarker = String(marker);
  });
};
